import React, { useEffect, useState } from 'react';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { IoShieldCheckmarkOutline, IoCloudOutline, IoColorPaletteOutline } from 'react-icons/io5';
import { FaChevronUp } from 'react-icons/fa';
import MatterLogo from '../images/matter.png';
import { useLanguage } from '../pages/LanguageContext';

const faqList = {
  en: [
    {
      q: 'What is Matter?',
      a: 'Matter is an open-source smart home standard developed by the CSA . It enables secure, reliable, and cross-brand interoperability for smart devices.',
    },
    {
      q: 'Why choose Matter-enabled devices?',
      a: 'Matter simplifies installation, ensures compatibility between brands, allows local (offline) control, and prioritizes end-to-end data encryption.',
    },
    {
      q: 'What devices are supported?',
      a: 'Smart lights, plugs, door locks, sensors, thermostats, and more with growing adoption from leading manufacturers worldwide.',
    },
  ],
  ar: [
    {
      q: 'ما هو ماتر؟',
      a: 'ماتر هو معيار مفتوح المصدر للمنازل الذكية، طورته CSA. يتيح اتصالًا آمنًا وموثوقًا وقابلًا للتشغيل المتبادل بين الأجهزة الذكية',
    },
    {
      q: 'لماذا أختار أجهزة تدعم ماتر؟',
      a: 'ماتر يُبسّط عملية التركيب، ويضمن التوافق بين مختلف العلامات التجارية، ويدعم التحكم المحلي مع حماية البيانات بتشفير شامل.',
    },
    {
      q: 'ما هي الأجهزة المدعومة؟',
      a: 'الأضواء الذكية، المقابس، أقفال الأبواب، المستشعرات، منظمات الحرارة، وغيرها — مع تزايد دعم الشركات المصنّعة الرائدة حول العالم',
    },
  ]
};

const features = {
  en: [
    {
      icon: <IoShieldCheckmarkOutline />,
      title: 'Built-In Security',
      desc: 'Matter uses advanced encryption protocols to protect your smart home data from unauthorized access.'
    },
    {
      icon: <IoCloudOutline />,
      title: 'Works Offline',
      desc: 'Devices remain responsive even without internet thanks to Matter’s local control capabilities.'
    },
    {
      icon: <IoColorPaletteOutline />,
      title: 'Unified Ecosystem',
      desc: 'No more compatibility headaches. Control your devices through Apple, Google, Alexa, and more using one app or voice assistant.'
    }
  ],
  ar: [
    {
      icon: <IoShieldCheckmarkOutline />,
      title: 'أمان مدمج',
      desc: 'ماتر يستخدم بروتوكولات تشفير متقدمة لحماية بيانات منزلك الذكي من الوصول غير المصرح به'
    },
    {
      icon: <IoCloudOutline />,
      title: 'يعمل دون إنترنت',
      desc: 'تبقى الأجهزة مستجيبة حتى دون اتصال بالإنترنت بفضل قدرات التحكم المحلي'
    },
    {
      icon: <IoColorPaletteOutline />,
      title: 'نظام موحد',
      desc: 'وداعًا لمشاكل التوافق. تحكم في أجهزتك باستخدام Apple أو Google أو Alexa من خلال تطبيق أو مساعد صوتي واحد'
    }
  ]
};

const titles = {
  en: {
    main: "Matter Smart Home Standard",
    subtitle: "Future-proof your smart home . Matter is the new universal standard that ensures your devices work seamlessly together, securely and reliably across platforms like Apple, Google, and Amazon.",
    faq: "Matter FAQ"
  },
  ar: {
    main: "معيار ماتر للمنازل الذكية",
    subtitle: "اجعل منزلك الذكي مستقبليًا. ماتر هو المعيار العالمي الجديد الذي يضمن تواصل أجهزتك الذكية بسلاسة وأمان عبر أنظمة مثل Apple وGoogle وAmazon",
    faq: "الأسئلة الشائعة عن ماتر"
  }
};

const MatterPage = () => {
  const [showButton, setShowButton] = useState(false);
  const { language } = useLanguage();

  useEffect(() => {
    AOS.init({ duration: 1000, once: true });
    const handleScroll = () => setShowButton(window.scrollY > 300);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      <style>{`
        body.dark, .dark .matter-main-bg { background:#111 !important; color: #fff !important; }
        .dark .matter-header,
        .dark .matter-faq-card,
        .dark .matter-faq-title {
          background:#111 !important;
          color: #fff !important;
        }
        .dark .matter-header h1,
        .dark .matter-header h2,
        .dark .matter-header h3,
        .dark .matter-faq-title {
           #1b4fbdff !important;
          background: none !important;
          -webkit-background-clip: unset !important;
          -webkit-text-fill-color: #1b4fbdff !important;
        }
        body.dark .matter-logo, .dark .matter-logo {
          filter: brightness(0) invert(1);
        }
        .dark .matter-feature-card {
          background: #000000ff !important;
          color: #fff !important;
          border: 1.5px solid #fff !important;
          box-shadow: 0 10px 32px #2297e62a !important;
        }
        .dark .matter-feature-card h3 {
          color:  #1b4fbdff !important;
        }
        .dark .matter-feature-card p {
          color: #ffffffff !important;
        }
        .dark .matter-feature-card .matter-icon {
          color: #ffffffff !important;
        }
        .dark .matter-faq-card details {
          background: #000000ff !important;
          color: #fff !important;
          border: 1.2px solid #fff !important;
        }
        .dark .matter-faq-card summary {
          color: #1b4fbdff !important;
          font-weight: 700;
        }
        .dark .matter-faq-card p {
          color: #ffffffff !important;
        }
        .matter-main-bg { max-width: 100vw; overflow-x: hidden; font-family: 'Inter', 'Poppins', Arial, sans-serif;}
        .matter-header { text-align: center; padding: 4rem 2rem 2rem; background: #fff; }
        .matter-logo { width: 180px; height: auto; margin-bottom: 1rem; transition: filter 0.2s; }
        .matter-logo-title { font-size: 2.2rem; font-weight: 700; margin-bottom: 0.5rem; color: #1a4bb7;}
        .matter-logo-subtitle { font-size: 1rem;  max-width: 600px; margin: 0 auto; }
        .matter-features { display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 2.5rem; padding: 5rem 2rem; max-width: 1100px; margin: 0 auto; }
        .matter-feature-card { background: #fff; border-radius: 18px; padding: 2rem; box-shadow: 0 12px 24px rgba(0,0,0,0.05); text-align: center; border: 1.5px solid #e3eefd; transition: transform .17s, box-shadow .17s; }
        .matter-feature-card:hover { transform: translateY(-7px) scale(1.03); box-shadow: 0 16px 38px #2297e61b;}
        .matter-feature-card .matter-icon { font-size: 3.2rem;  margin-bottom: 1rem; }
        .matter-feature-card h3 { font-size: 1.4rem; font-weight: 700; margin-bottom: 0.6rem; color: #1a4bb7;}
        .matter-feature-card p { font-size: 1rem; line-height: 1.7; color: #555;}
        .matter-faq { max-width: 800px; margin: 0 auto 5rem; padding: 0 1rem;}
        .matter-faq-title { text-align: center; font-size: 2rem; font-weight: 700; margin-bottom: 2rem; color: #1a4bb7;}
        .matter-faq-card details { background: #fff; border-radius: 12px; padding: 1rem 1.5rem; margin-bottom: 1rem; box-shadow: 0 8px 20px rgba(0,0,0,0.05); cursor: pointer; border: 1.3px solid #e3eefd;}
        .matter-faq-card details[open] { background: #f8fbff;}
        .matter-faq-card summary { font-size: 1.1rem; font-weight: 600; list-style: none; cursor: pointer;}
        .matter-faq-card p { font-size: 0.95rem; color: #444; margin-top: 0.6rem; line-height: 1.6;}
        .matter-back-btn { position: fixed; bottom: 40px; left: 25px; width: 45px; height: 45px; border-radius: 50%; background-color: #1a4bb7; color: #fff; border: none; font-size: 18px; font-weight: bold; display: flex; justify-content: center; align-items: center; box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3); cursor: pointer; z-index: 999; transition: opacity 0.3s, transform 0.3s;}
        .matter-cta { background: #f0f7ff; padding: 4rem 2rem; text-align: center; border-top: 1.5px solid #dbeafe; }
        .matter-cta h2 { font-size: 2rem; font-weight: 700; color:  #1b4fbdff;margin-bottom: 1rem; }
        .matter-cta p { font-size: 1rem; color: #333; max-width: 700px; margin: 0 auto 2rem; line-height: 1.7; }
        .matter-cta-button { display: inline-block; background: #1a4bb7; color: #fff; padding: 0.8rem 1.6rem; font-weight: 600; border-radius: 8px; text-decoration: none; transition: background 0.3s; }
        .matter-cta-button:hover { background: #1553b8; }
        .dark .matter-cta { background: #101c33; border-top: 1.5px solid  #ffffffff }
        .dark .matter-cta h2 { color: #1b4fbdff; }
        .dark .matter-cta p { color: #ffffffff; }
        .dark .matter-cta-button { background:  #1b4fbdff; }
        .dark .matter-cta-button:hover { background:  #1b4fbdff; }
        @media (max-width: 700px) {
          .matter-features { grid-template-columns: 1fr; gap: 1.2rem; padding: 2.5rem 0.2rem; }
          .matter-header { padding: 2rem 0.6rem 1rem; }
        }
      `}</style>
      <main
        className="matter-main-bg"
        style={{
          direction: language === "ar" ? "rtl" : "ltr",
          fontFamily: language === "ar"
            ? "'Cairo', 'Tajawal', Tahoma, Arial, sans-serif"
            : "'Inter', 'Poppins', Arial, sans-serif"
        }}>
        <section className="matter-header" data-aos="zoom-in">
          <img src={MatterLogo} alt="Matter Logo" className="matter-logo" />
          <h1 className="matter-logo-title">{titles[language].main}</h1>
          <p className="matter-logo-subtitle">{titles[language].subtitle}</p>
        </section>

        <section className="matter-features">
          {features[language].map((item, i) => (
            <div key={i} className="matter-feature-card" data-aos="fade-up" data-aos-delay={i * 200}>
              <div className="matter-icon">{item.icon}</div>
              <h3>{item.title}</h3>
              <p>{item.desc}</p>
            </div>
          ))}
        </section>

        <section className="matter-faq" data-aos="fade-up">
          <h2 className="matter-faq-title">{titles[language].faq}</h2>
          <div className="matter-faq-card">
            {faqList[language].map((item, i) => (
              <details key={i}>
                <summary>{item.q}</summary>
                <p>{item.a}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="matter-cta" data-aos="fade-up">
          <h2>{language === 'ar' ? 'جاهز للانتقال إلى ماتر؟' : 'Ready to Matter?'}</h2>
          <p>
            {language === 'ar'
              ? 'دع خبراءنا يساعدونك في دمج هذا المعيار الجديد في منزلك الذكي. تواصل معنا الآن وابدأ التحول الحقيقي'
              : 'Let us help you integrate the future of smart living . Talk to our engineers and bring your home up to the new standard.'}
          </p>
          <a href="/contact" className="matter-cta-button">
            {language === 'ar' ? '👈 ابدأ مع ماتر' : '👉 Get Started with Matter and contact us now'}
          </a>
        </section>

        {showButton && (
          <button onClick={scrollToTop} className="matter-back-btn">
            <FaChevronUp />
          </button>
        )}
      </main>
    </>
  );
};

export default MatterPage;
